import React from 'react'
import { Subheading, H3, Button, InfoSheetPanel, Row, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { ControlHighlight } from '@fs/zion-icon'
import { getNestedElementById, useSlsHighlightsContext } from '@fs/zion-sls'
import HighlightTips from '../../../indexing/components/fieldComponents/HighlightTips/HighlightTips'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import { getEventFieldIds } from '../../utils/utils'
import { showEventsHighlights, showEssentialInfoHighlights } from '../../utils/highlightUtils'

export default function EditHighlights({ editHighlightsOverlay, editEventOverlay, editPersonOverlay, currentTask }) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()

  const resetEventHighlights = (highlights) => {
    const event = getNestedElementById(currentTask?.subElements, state.selectedEventId)
    const fieldIds = event ? [...getEventFieldIds(event), event.id] : Object.values(state.addNewEvent)
    showEventsHighlights({
      h,
      currentTask,
      highlights,
      forceFieldIds: fieldIds, // doing this to make sure we get the new highlights added from Add New Event
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const isStepOne = state.currentStep === STEPS.STEP_ONE
  const isStepTwo = state.currentStep === STEPS.STEP_TWO
  const isStepThree = state.currentStep === STEPS.STEP_THREE
  const isStepFour = state.currentStep === STEPS.STEP_FOUR

  const resetHighlights = (highlights) => {
    if (isStepThree || (isStepFour && editEventOverlay.isOpen)) {
      resetEventHighlights(highlights)
    }

    if (isStepTwo || (isStepFour && editPersonOverlay.isOpen))
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [state.selectedPersonId],
        highlights,
        isSmallScreen,
      })

    if (isStepOne)
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [state.selectedPersonId],
        highlights,
        isSmallScreen,
      })
  }

  const saveButtonClicked = () => {
    const updatedHighlightArrays = h.stageEdits()
    editHighlightsOverlay.close()
    resetHighlights([...updatedHighlightArrays.mainEditHighlights, ...updatedHighlightArrays.splitScreenEditHighlights])
  }

  const cancelButtonButtonClicked = () => {
    editHighlightsOverlay.close()
    h.setActiveFieldInfo({ id: null, value: null })

    // Without calling h.stageEdits, the displayHighlight changes are not retained
    resetHighlights([...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights])
  }
  // The back arrow on the panel was clicked
  const userClosedPanel = cancelButtonButtonClicked

  const createHighlight = () => {
    dispatch({
      type: 'SET_CREATING_RECT',
      value: true,
    })
  }

  return (
    <InfoSheetPanel
      {...{ ...editHighlightsOverlay, close: userClosedPanel }}
      headingColor="yellow"
      title={
        state.isAddingHighlight
          ? t('editHighlight.panelHeader.panelHeadingAdd', 'Add Highlight')
          : t('editHighlight.panelHeader.panelHeadingEdit', 'Edit Highlight')
      }
      footer={
        <Row alignX="end">
          <Button
            onClick={cancelButtonButtonClicked}
            linkName="Edit Highlights Cancel"
            data-testid="edit-highlights-cancel-button"
          >
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={saveButtonClicked}
            linkName="Edit Highlights Save"
            data-testid="edit-highlights-save-button"
          >
            {t('edit-index.save.button', 'Save')}
          </Button>
        </Row>
      }
    >
      {editHighlightsOverlay.isOpen && (
        <>
          <H3 size="H5">
            {state.isAddingHighlight
              ? t('editHighlight.panelHeader.panelHeadingAdd', 'Add Highlight')
              : t('editHighlight.panelHeader.panelHeadingEdit', 'Edit Highlight')}
            <Subheading>
              {t('editHighlight.panelHeader.panelCaption', 'Select the blue highlights or create your own')}
            </Subheading>
          </H3>
          <Button
            Icon={ControlHighlight}
            onClick={createHighlight}
            data-testid="editHighlightTipsNewHighlightButton"
            linkName="Edit Highlights New Highlight"
          >
            {t('editHighlight.highlightTips.newHighlight', 'New Highlight')}
          </Button>
          <HighlightTips />
        </>
      )}
    </InfoSheetPanel>
  )
}
